import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChangeRoleRequest, InviteRequest } from '@agentisai/shared-types';
import { useAuth } from '../auth/AuthProvider';
import {
  changeMemberRole,
  inviteMember,
  listMembers,
  removeMember,
  withdrawInvite,
} from './client';
import { keys } from './keys';

/** The Users & Access roster + pending invites (D15-12). Owner/Admin — the API 403s a Member. */
export function useMembers() {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.members.list(),
    queryFn: ({ signal }) => listMembers({ getAccessToken, signal }),
    select: (res) => res.data,
  });
}

/**
 * A label for an Auth0 `sub` from the roster. `prefer: 'email'` (the tables and CSVs, where
 * a unique, recognisable address beats a display name) or `'name'` (prose, e.g. the activity
 * feed); each falls back to the other. `system` is the platform itself (budget alerts,
 * ADR-0078 §3). A sub missing from a loaded roster has left the organisation; the raw id
 * shows only if the roster can't load.
 */
export function useMemberName(prefer: 'name' | 'email' = 'name'): (sub: string) => string {
  const members = useMembers();
  const bySub = new Map(
    members.data?.members.map((m) => [
      m.userId,
      prefer === 'email' ? m.email || m.name : m.name || m.email,
    ]),
  );
  return (sub) => {
    if (sub === 'system') return 'Agentis';
    if (bySub.has(sub)) return bySub.get(sub) || sub;
    return members.data ? 'Former member' : sub;
  };
}

/**
 * The three writes (D15-13). Pending UI + invalidate, not an optimistic edit (R7):
 * each one can 409 (self, last Owner, already invited), so the roster refetches
 * whatever happened.
 */
function useMembersMutation<V>(
  mutationKey: readonly unknown[],
  fn: (vars: V, getAccessToken: () => Promise<string>) => Promise<unknown>,
) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey,
    mutationFn: (vars: V) => fn(vars, getAccessToken),
    onSettled: () => queryClient.invalidateQueries({ queryKey: keys.members.list() }),
  });
}

export function useInviteMember() {
  return useMembersMutation(keys.members.invite(), (body: InviteRequest, getAccessToken) =>
    inviteMember(body, { getAccessToken }),
  );
}

export function useChangeMemberRole() {
  return useMembersMutation(
    keys.members.changeRole(),
    ({ userId, role }: { userId: string } & ChangeRoleRequest, getAccessToken) =>
      changeMemberRole(userId, { role }, { getAccessToken }),
  );
}

/** Withdraw a pending invite by its opaque invitation id (Owner or Admin). */
export function useWithdrawInvite() {
  return useMembersMutation(keys.members.withdraw(), (invitationId: string, getAccessToken) =>
    withdrawInvite(invitationId, { getAccessToken }),
  );
}

export function useRemoveMember() {
  return useMembersMutation(keys.members.remove(), (userId: string, getAccessToken) =>
    removeMember(userId, { getAccessToken }),
  );
}
